import { useMemo, useState } from 'react';

import { useAlert } from 'contexts/alert/useAlert';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import {
  AllessehContentType,
  ArticleItem,
  ModuleInputAttributes,
  NewsletterDto,
  NewsletterInput,
  NewsletterPageContainer,
  PublicationSetting,
  ScheduledContent,
  ScheduledContentCancelInput,
  ScheduledContentCreateInput,
  ScheduledContentRescheduleInput,
  useCancelScheduledContentMutation,
  useCreateNewsletterMutation,
  useCreatePreviewNewsletterMutation,
  useRescheduleContentMutation,
  useScheduleContentMutation
} from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { formatInTimeZone } from 'utils/dates';
import { prepContentItems } from 'utils/prepPageModulesForPublish';

export const useNewsletterPublish = (publicationSettings?: PublicationSetting) => {
  const { alertError, removeAlert, alertSuccess } = useAlert();
  const currentProperty = useConvertedProperty();
  const { root: data, metadata, getDTO } = useDataModelContext<NewsletterPageContainer>();

  const { mutateAsync: sendTestAsync } = useCreatePreviewNewsletterMutation();
  const { mutateAsync: sendNowAsync } = useCreateNewsletterMutation();
  const { mutateAsync: scheduleAsync } = useScheduleContentMutation();
  const { mutateAsync: rescheduleAsync } = useRescheduleContentMutation();
  const { mutateAsync: cancelScheduleAsync } = useCancelScheduledContentMutation();

  const prepedNewsletterInput = useMemo(() => {
    const collection = data.collection.map(({ attributes, collection, type }) => ({
      attributes: attributes as ModuleInputAttributes,
      collection: collection.map((item) => prepContentItems(item as ArticleItem)),
      type
    }));

    return {
      allessehId: metadata.allessehId,
      attributes: data.attributes,
      collection,
      name: metadata.name,
      publicationKey: metadata.publicationKey
    };
  }, [data, metadata]);

  const [validationErrors, setValidationErrors] = useState<{ subject?: string; preHeaderText?: string }>({});

  const validateRequiredFields = () => {
    const errors: { subject?: string; preHeaderText?: string } = {};
    if (!data.attributes.subject.trim()) {
      errors.subject = 'Subject is required.';
    }
    if (!data.attributes.preHeaderText.trim()) {
      errors.preHeaderText = 'Pre-Header Text is required.';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const clearValidationErrors = () => setValidationErrors({});

  const handleSendTest = async (emails: string) => {
    if (!validateRequiredFields()) {
      return;
    }

    clearValidationErrors();
    removeAlert();

    const newsletterInput = {
      ...prepedNewsletterInput,
      attributes: { ...data.attributes, recipients: emails }
    };

    try {
      await sendTestAsync({ newsletterInput });

      alertSuccess(
        `Preview campaign ${newsletterInput.allessehId} successfully published to Allesseh for recipient(s) ${emails
          .split(',')
          .join(', ')}.`
      );
    } catch (e: unknown) {
      if (e instanceof Error) {
        alertError(`Could not update the newsletter. ${e.message}`);
      } else {
        alertError('Could not update the newsletter.');
      }
    }
  };

  const handleSendNow = async (): Promise<NewsletterDto | null> => {
    if (!validateRequiredFields()) {
      return null;
    }

    clearValidationErrors();
    removeAlert();

    const newsletterInput: NewsletterInput = prepedNewsletterInput;

    try {
      const { createNewsletter } = await sendNowAsync({ newsletterInput });

      alertSuccess(
        `Campaign successfully sent on ${formatInTimeZone(
          createNewsletter.metadata.createdUtc,
          publicationSettings?.preferredTimezone,
          'MMMM dd, yyyy',
          { showTimezoneAbbreviation: false }
        )} at ${formatInTimeZone(
          createNewsletter.metadata.createdUtc,
          publicationSettings?.preferredTimezone,
          'h:mm aaaa',
          {
            showTimezoneAbbreviation: true
          }
        )}.`
      );

      return createNewsletter as NewsletterDto;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      alertError(`Campaign failed with the following error: ${message}`);
    }

    return null;
  };

  const handleSchedule = async (publishUtc: number): Promise<ScheduledContent | null> => {
    if (!validateRequiredFields()) {
      return null;
    }

    clearValidationErrors();
    const newsletter = getDTO(true);
    newsletter.metadata.publishUtc = publishUtc;

    removeAlert();

    const scheduleInput: ScheduledContentCreateInput = {
      allessehId: metadata.allessehId,
      contentType: AllessehContentType.Newsletter,
      publishUtc,
      body: newsletter
    };

    try {
      const { scheduleContent } = await scheduleAsync({
        scheduledContentCreateInput: scheduleInput,
        publicationKey: currentProperty ?? ''
      });

      alertSuccess(
        `Campaign successfully scheduled for ${formatInTimeZone(
          scheduleContent.publishUtc,
          publicationSettings?.preferredTimezone,
          'MMMM dd, yyyy',
          { showTimezoneAbbreviation: false }
        )} at ${formatInTimeZone(scheduleContent.publishUtc, publicationSettings?.preferredTimezone, 'h:mm aaaa', {
          showTimezoneAbbreviation: true
        })}.`
      );

      return scheduleContent as ScheduledContent;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      alertError(`Could not schedule the newsletter. ${message}`);
    }

    return null;
  };

  const handleReschedule = async (publishUtc: number): Promise<ScheduledContent | null> => {
    removeAlert();

    const newsletter = getDTO(true);

    const previousPublishUtc = newsletter.metadata.publishUtc!;
    newsletter.metadata.publishUtc = publishUtc;

    const input: ScheduledContentRescheduleInput = {
      allessehId: newsletter.metadata.allessehId,
      contentType: AllessehContentType.Newsletter,
      previousPublishUtc,
      newPublishUtc: publishUtc,
      body: newsletter
    };

    try {
      const { rescheduleContent } = await rescheduleAsync({
        publicationKey: currentProperty ?? '',
        scheduledContentRescheduleInput: input
      });

      alertSuccess('Rescheduled newsletter module has been updated.');

      return rescheduleContent as ScheduledContent;
    } catch (e: unknown) {
      if (e instanceof Error) {
        alertError(`Could not update the schedule the newsletter module. ${e.message}`);
      } else {
        alertError('Could not update the schedule the newsletter module.');
      }
    }

    return null;
  };

  const handleDeleteSchedule = async () => {
    const newsletter = getDTO(true);

    const input: ScheduledContentCancelInput = {
      allessehId: newsletter.metadata.allessehId,
      publishUtc: newsletter.metadata.publishUtc!
    };

    try {
      await cancelScheduleAsync({ publicationKey: currentProperty ?? '', scheduledContentCancelInput: input });

      alertSuccess('Campaign successfully canceled.');
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      alertError(`Cancel campaign failed with the following error: ${message}`);
    }
  };

  const handleSendSchedule = async () => {
    removeAlert();

    const newsletter = getDTO(true);

    const newsletterInput: NewsletterInput = prepedNewsletterInput;

    const input: ScheduledContentCancelInput = {
      allessehId: newsletter.metadata.allessehId,
      publishUtc: newsletter.metadata.publishUtc!
    };

    try {
      await cancelScheduleAsync({ publicationKey: currentProperty ?? '', scheduledContentCancelInput: input });

      const { createNewsletter } = await sendNowAsync({ newsletterInput });

      alertSuccess(
        `Campaign successfully sent on ${formatInTimeZone(
          createNewsletter.metadata.createdUtc,
          publicationSettings?.preferredTimezone,
          'MMMM dd, yyyy',
          { showTimezoneAbbreviation: false }
        )} at ${formatInTimeZone(
          createNewsletter.metadata.createdUtc,
          publicationSettings?.preferredTimezone,
          'h:mm aaaa',
          { showTimezoneAbbreviation: true }
        )}.`
      );
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      alertError(`Send campaign failed with the following error: ${message}`);
    }
  };

  return {
    handleSendTest,
    handleSendNow,
    handleSchedule,
    handleReschedule,
    handleSendSchedule,
    handleDeleteSchedule,
    validationErrors
  };
};
